import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, CheckCircle2, Store } from "lucide-react";
import { passwordError, PASSWORD_HINT } from "@/lib/password";

export default function ResetPassword({ invite = false }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { slug } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [shopPublic, setShopPublic] = useState(null);
  const navigate = useNavigate();
  const [linkStatus, setLinkStatus] = useState(null);
  const [newLinkSent, setNewLinkSent] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const title = invite ? "Criar minha senha" : "Redefinir senha";

  useEffect(() => {
    if (!token) return;
    api.get("/auth/link-status", { params: { token } }).then(({ data }) => setLinkStatus(data)).catch(() => setLinkStatus({ status: "invalid" }));
  }, [token]);

  const requestNew = async () => {
    setRequesting(true);
    try { await api.post("/auth/request-new-link", { token }); setNewLinkSent(true); }
    catch (err) { setError(formatApiError(err)); }
    finally { setRequesting(false); }
  };

  useEffect(() => {
    if (!slug) { setShopPublic(null); return; }
    (async () => {
      try { const { data } = await api.get(`/shops/public/by-slug/${slug}`); setShopPublic(data); }
      catch { setShopPublic(false); }
    })();
  }, [slug]);

  useEffect(() => {
    applyBrandColor(shopPublic?.brand_color || DEFAULT_BRAND_COLOR);
    return () => applyBrandColor(DEFAULT_BRAND_COLOR);
  }, [shopPublic?.brand_color]);

  useEffect(() => {
    const base = "AutoOficina";
    document.title = shopPublic?.name ? `${shopPublic.name} — ${title}` : base;
    return () => { document.title = base; };
  }, [shopPublic?.name]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (passwordError(password)) { setError(passwordError(password)); return; }
    if (password !== confirm) { setError("As senhas não coincidem"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate(slug ? `/o/${slug}` : "/login", { replace: true }), 1500);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const hasShopLogo = !!(shopPublic?.id && shopPublic?.logo_path);
  const shopName = shopPublic?.name || "";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {(hasShopLogo || shopName) && (
          <div className="mb-8 text-center">
            {hasShopLogo ? (
              <img src={shopLogoUrl(shopPublic.id)} alt={shopName} data-testid="reset-shop-logo"
                   className="mx-auto h-16 sm:h-20 w-auto" />
            ) : (
              <div data-testid="reset-shop-name" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-card2-border bg-card2">
                <Store className="h-5 w-5" style={{ color: "var(--c-brand)" }} />
                <span className="font-display font-black uppercase text-zinc-100">{shopName}</span>
              </div>
            )}
          </div>
        )}
        <div data-testid="reset-password-card" className="w-full rounded-2xl border border-card2-border bg-card2 backdrop-blur-md p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--c-brand)" }} />
            <span className="text-xs font-mono-caps text-zinc-400">{title}</span>
          </div>
          {token && linkStatus && linkStatus.status !== "valid" && !done ? (
            <div data-testid="link-unusable" className="space-y-3 text-sm">
              <p className="text-amber-200">{linkStatus.status === "expired" ? "Este link venceu." : linkStatus.status === "used" ? "Este link já foi usado ou foi substituído por um novo." : "Link inválido."}</p>
              {(linkStatus.kind === "invite" && linkStatus.status !== "invalid") ? (
                newLinkSent
                  ? <p data-testid="new-link-sent" style={{ color: "var(--c-brand-neon)" }}>Pronto! Enviamos um link novo para o seu e-mail.</p>
                  : <Button data-testid="request-new-link-btn" onClick={requestNew} disabled={requesting} style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }} className="w-full h-11 font-bold uppercase">{requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pedir um link novo"}</Button>
              ) : (
                <p className="text-zinc-400">Volte à tela de login e use "Esqueci minha senha".</p>
              )}
              {error && <div data-testid="reset-error" className="text-sm text-red-400">{error}</div>}
            </div>
          ) : !token ? (
            <p data-testid="reset-missing-token" className="text-sm text-red-400">Link inválido. Abra o link enviado por e-mail.</p>
          ) : done ? (
            <div data-testid="reset-success" className="text-sm flex items-center gap-2" style={{ color: "var(--c-brand-neon)" }}>
              <CheckCircle2 className="h-4 w-4" /> {invite ? "Senha criada." : "Senha redefinida."} Entrando…
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Nova senha</Label>
                <Input data-testid="reset-password-input" type="password" required minLength={8} value={password} placeholder={PASSWORD_HINT}
                       onChange={(e) => setPassword(e.target.value)}
                       className="mt-1 h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[var(--c-brand)]" />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Confirmar</Label>
                <Input data-testid="reset-password-confirm" type="password" required minLength={8} value={confirm}
                       onChange={(e) => setConfirm(e.target.value)}
                       className="mt-1 h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[var(--c-brand)]" />
              </div>
              {error && <div data-testid="reset-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-md px-3 py-2">{error}</div>}
              <Button data-testid="reset-submit" type="submit" disabled={loading}
                      style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}
                      className="w-full h-12 font-bold uppercase hover:brightness-110">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : title}
              </Button>
            </form>
          )}
        </div>
      </div>
      {shopName && (
        <p data-testid="reset-powered-by" className="mt-10 text-[11px] font-mono-caps text-zinc-600 tracking-widest">
          feito com AutoOficina
        </p>
      )}
    </div>
  );
}
